import api from "apis";
import { CheckinsProp } from "apis/types/checkin";
import {
  AssignPunchBody,
  MissingPunchParams,
  MissingPunchShift,
  RecalculateBody,
  RecalculateResponse,
  ShiftPunchDetails,
  ShiftPunchesParams,
  UnassignPunchBody,
} from "apis/types/timekeeper";

export const timekeeperApi = api.injectEndpoints({
  endpoints: (build) => ({
    // Unresolved shifts only — this backs the Missing Punch list and its
    // filters, and deliberately stays blind to completed shifts.
    getMissingPunches: build.query<MissingPunchShift[], MissingPunchParams>({
      query: (params) => ({
        url: "/timekeeper/missing-punch/",
        params: Object.fromEntries(
          Object.entries(params).filter(([_, v]) => v !== undefined && v !== ""),
        ),
      }),
      providesTags: ["Timekeeper"],
    }),

    // Shift-scoped punch details. Answers for complete, missing and absent
    // shifts alike, and returns every punch in the retrieval window, so the
    // punches modal reads from here instead of from missing-punch.
    getShiftPunches: build.query<ShiftPunchDetails, ShiftPunchesParams>({
      query: ({ shift_id }) => ({
        url: "/timekeeper/shift-punches/",
        params: { shift_id },
      }),
      providesTags: ["Timekeeper"],
    }),

    assignPunch: build.mutation<CheckinsProp, AssignPunchBody>({
      query: (body) => ({
        url: "/timekeeper/assign-punch/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Timekeeper", "EmployeeShiftS", "Shift"],
    }),

    unassignPunch: build.mutation<CheckinsProp, UnassignPunchBody>({
      query: (body) => ({
        url: "/timekeeper/unassign-punch/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Timekeeper", "EmployeeShiftS", "Shift"],
    }),

    // Tags are only invalidated on success, so the tables refresh after a
    // completed recalculation and stay put on a backend error.
    recalculate: build.mutation<RecalculateResponse, RecalculateBody>({
      query: (body) => ({
        url: "/timekeeper/recalculate/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Timekeeper", "EmployeeShiftS", "Shift"],
    }),
  }),
});

export const {
  useGetMissingPunchesQuery,
  useGetShiftPunchesQuery,
  useAssignPunchMutation,
  useUnassignPunchMutation,
  useRecalculateMutation,
} = timekeeperApi;
